import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { isAndroid } from 'react-device-detect';

import { OuterContainer } from '../DashboardOuterContainerPagesLayout';
import joistCoverFrame from '../../../assets/images/joist_cover_frame.png';
import { ReactComponent as JoistLogo } from '../../../assets/images/joist_logo.svg';
import ReviewsDesktopSample from '../../../assets/images/joist_reviews_desktop_sample.png';
import { Content } from '../../../common/models/content/Content';
import { useAppDispatch } from '../../../store/hooks';
import { useIsSmallScreen } from '../../../common/hooks/useResponsive';
import { getContentByTitle } from '../../../store/thunks/contentThunk';
import { TestIds } from '../../../assets/testIds';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';

export default function AccountJoist() {
  const { t } = useTranslation();
  const { trackEdgeSolutionOrgHighlight } = useCustomEventTracking();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [featuredContent, setFeaturedContent] = useState<Array<Content | undefined> | null>(null);
  const isDesktopScreen = !useIsSmallScreen(); // Inverted logic: not small screen = desktop
  const DESKTOP_CARD_DESCRIPTION_LIMIT = 150;
  const MOBILE_CARD_DESCRIPTION_LIMIT = 80;
  const ctaUrl = isAndroid ? 'joist://settings/reviews' : 'https://support.joistapp.com/en/articles/9731199-joist-reviews';

  const formatShortDescription = (description: string) => {
    const count = isDesktopScreen ? DESKTOP_CARD_DESCRIPTION_LIMIT : MOBILE_CARD_DESCRIPTION_LIMIT;
    return description?.length > count ? `${description?.substring(0, count)}...` : description;
  };

  useEffect(() => {
    Promise.all([
      dispatch(getContentByTitle('Turning Negative Feedback Into a Win: How to Handle a Negative Google Review Response')).then(unwrapResult).then((res) => res[0]),
      dispatch(getContentByTitle('5 Tips for Using Review Management Software to Improve Your Home Services Business')).then(unwrapResult).then((res) => res[0]),
      dispatch(getContentByTitle('How to Get Customers to Leave Reviews for Your Local Home Services Business')).then(unwrapResult).then((res) => res[0]),
      dispatch(getContentByTitle('The Easy Way To Get More 5-Star Google Reviews')).then(unwrapResult).then((res) => res[0]),
    ])
      .then((fetchedContent) => {
        setFeaturedContent(fetchedContent);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OuterContainer
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: `${theme.spacing(3)} !important`,
        [theme.breakpoints.up('md')]: {
          padding: `${theme.spacing(0)} !important`,
          paddingTop: `${theme.spacing(4)} !important`,
          paddingLeft: `${theme.spacing(3)} !important`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Box
          data-testid={TestIds.joist.coverFrame}
          sx={{
            borderRadius: theme.spacing(1.5),
            background: `linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), url(${joistCoverFrame}) lightgray 50% / cover no-repeat`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: theme.spacing(24.75),
            width: '100%',
            [theme.breakpoints.down('lg')]: {
              height: theme.spacing(15),
            },
          }} />
        <Box
          sx={{
            display: 'flex',
            width: '70%',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(4),
            position: 'relative',
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: theme.spacing(-3.75),
              [theme.breakpoints.down('lg')]: {
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                marginTop: theme.spacing(-12.5),
              },
            }}
          >
            <Box
              data-testid={TestIds.joist.logo}
              sx={{
                display: 'flex',
                width: theme.spacing(18.75),
                height: theme.spacing(18.75),
                flexDirection: 'column',
                flexShrink: 0,
                justifyContent: 'center',
                alignItems: 'center',
                gap: theme.spacing(1.25),
                borderRadius: theme.spacing(12.5),
                border: `${theme.spacing(0.5)} solid ${theme.palette.common.white}`,
                background: theme.palette.common.white,
                boxShadow: `0 ${theme.spacing(0.25)} ${theme.spacing(1)} 0 rgba(0, 0, 0, 0.10)`,
                marginRight: theme.spacing(2.5),
              }}
            >
              {JoistLogo && <JoistLogo />}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                marginRight: theme.spacing(1.25),
                gap: theme.spacing(1.5),
              }}
            >
              <Typography variant='h2' sx={{ alignSelf: 'stretch' }}>Joist</Typography>
              <Typography variant='body1' sx={{
                alignSelf: 'stretch',
                color: theme.palette.grey[600],
              }}>
                {t('accountJoist.heading1')}
                <br />
                {t('accountJoist.heading2')}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              padding: theme.spacing(4),
              alignItems: 'center',
              gap: theme.spacing(1.5),
              flex: ' 1 0 0',
              alignSelf: 'stretch',
              borderRadius: theme.spacing(1.5),
              background: theme.palette.brand.primary,
              boxShadow:
                `0 0 ${theme.spacing(0.25)} ${theme.spacing(0.125)} rgba(0, 0, 0, 0.04), 0 0 ${theme.spacing(0.5)} 0 rgba(0, 0, 0, 0.02), 0 0 ${theme.spacing(1)} 0 rgba(0, 0, 0, 0.04)`,
              color: theme.palette.common.white,
              [theme.breakpoints.down('lg')]: {
                flexWrap: 'wrap',
                justifyContent: 'center',
              },
            }}
          >
            <Box>
              <img src={ReviewsDesktopSample} alt="Log in" />
            </Box>
            <Box>
              <Typography variant='h3' marginBottom={theme.spacing(1.5)}>{t('accountJoist.reviewsTitle')}</Typography>
              <Typography variant='body8' marginBottom={theme.spacing(3)}>
                {t('accountJoist.reviewsDescription1')}
              </Typography>
              <Link
                id='joist-account-CTA'
                href={ctaUrl}
                target='_blank'
                data-testid={TestIds.joist.reviewsLink}
                onClick={() => {
                  trackEdgeSolutionOrgHighlight('Click', 'StartCollectingReviewCTA', { ctaUrl });
                }}
                sx={{
                  display: 'flex',
                  padding: theme.spacing(1, 2),
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  borderRadius: theme.spacing(3),
                  border: `${theme.spacing(0.25)} solid ${theme.palette.common.white}`,
                  background: theme.palette.common.white,
                  color: theme.palette.brand.primary,
                  marginTop: theme.spacing(2.5),
                  textDecoration: 'none',
                  width: 'fit-content',
                }}
              >
                <Typography variant='h6'>{`${t('button.startCollectingReviews')}`}</Typography>
                <ArrowForwardIcon fontSize="small" />
              </Link>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              padding: 0,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: theme.spacing(4),
              alignSelf: 'stretch',
              justifyContent: 'center',
            }}
          >
            <Typography variant='h2'>{t('accountJoist.featuredCardsHeading')}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                alignContent: ' flex-start',
                gap: theme.spacing(4),
                alignSelf: 'stretch',
                flexWrap: 'wrap',
              }}
            >
              {!featuredContent && <Container sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: theme.spacing(18.75),
              }}>
                <CircularProgress color="inherit" size={60} />
              </Container>}

              <Grid container spacing={2} >
                {featuredContent?.map((content, index) => (
                  content &&
                  <Grid item sm={12} md={6} key={index}>
                    <Link
                      id={`joist-article-link-${index}`}
                      href={
                        content.attributes?.external_link?.target ||
                        content.attributes?.video_url ||
                        '#'
                      }
                      sx={{
                        textDecoration: 'none',
                        color: theme.palette.common.black,
                        width: '100%',
                      }}
                      target='_blank'
                      data-testid={TestIds.joist.reviewsLink}
                      onClick={() => {
                        trackEdgeSolutionOrgHighlight('Click', 'ContentLink', {
                          contentId: content.id,
                          contentTitle: content.attributes?.title,
                          contentType: content.attributes?.type,
                        });
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        height: '100%',
                        padding: theme.spacing(2.5),
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        gap: theme.spacing(2.5),
                        borderRadius: theme.spacing(1.5),
                        border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
                        background: theme.palette.common.white,
                        boxShadow:
                          `0 ${theme.spacing(0.125)} ${theme.spacing(0.25)} 0 rgba(0, 0, 0, 0.05), 0 0 0 0 rgba(0, 0, 0, 0.00), 0 0 0 0 rgba(0, 0, 0, 0.00)`,
                      }}>
                        <Typography variant='h4'
                          sx={{
                            [theme.breakpoints.down('sm')]: {
                              fontSize: theme.spacing(1.75),
                              justifyContent: 'center',
                            },
                          }}
                        >
                          {content.attributes?.title}
                        </Typography>
                        <Typography variant='body8'
                          sx={{ color: theme.palette.grey[600] }}>
                          {formatShortDescription(content.attributes?.short_description)}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            height: theme.spacing(3.375),
                            padding: theme.spacing(1.5),
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: theme.spacing(0.625),
                            borderRadius: theme.spacing(18.75),
                            border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
                            boxShadow: `0 ${theme.spacing(0.125)} ${theme.spacing(0.25)} 0 rgba(0, 0, 0, 0.05), 0 0 0 0 rgba(0, 0, 0, 0.00), 0 0 0 0 rgba(0, 0, 0, 0.00)`,
                            textDecoration: 'none',
                            width: 'fit-content',
                            color: theme.palette.grey[600],
                            textAlign: 'center',
                            fontSize: theme.spacing(1.75),
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: theme.spacing(2.5),
                            overflow: 'hidden',
                          }}
                        >
                          {content.attributes.type === 'article' ? t('accountJoist.articleCta') : t('accountJoist.videoCta')}
                          <ArrowForwardIcon fontSize="small" />
                        </Box>
                      </Box>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </OuterContainer >
  );
}
